import React, { useId, useState } from 'react';
import styled from 'styled-components';
import { ActionCard, Card, Icon, MiniWeek, Note, type IconName } from '@repo/design-system';
import type { AuthoredOpeningHoursDay } from '@repo/frontend-types';
import type { ReadOpeningHoursOutcome } from '@repo/front-end-services';
import { semantic, space, typeRole } from '@repo/design-tokens';
import { IconDisc, visuallyHidden } from '@repo/ui';

import type { LaunchHoursState } from '../useLaunchHours';
import type { OrganisationTwins } from '../useOrganisationTwins';
import { startNoteFor } from '../launchHoursText';
import CopyFromTwin, { type CopySource } from './CopyFromTwin';
import { miniWeekOf, open247, sameEveryDay, weekdaysAndWeekends } from './hoursModel';
import { HOURS_STILL_SAVING, type HoursActionResult } from './useOpeningHoursEditor';

/**
 * **The start panel** — the Opening hours step for a twin with no standard hours covering today or
 * later (spec twin-details-page-hours-start, R2; Rob's frame 05, *Opening hours: first time*): *When
 * is {twin} open?*, the timezone the hours are kept in, four quick starts that each set a whole week
 * with one press, a quieter *Start on a later date instead*, and the launch rule's note.
 *
 * A press applies at once (the epic's question 7 default, ruled by Maria on 2026-10-19): the step's
 * `onStart` saves through the editor, and on success the week editor replaces this panel with focus
 * on its title. Anything else leaves focus on the pressed card.
 */

export interface StartPanelProps {
  /** `bidTwin.name`; null when the twin has none. */
  twinName: string | null;
  /** The read's zone; undefined when the read carried no `timezone` key (an older middleware). */
  timezone: string | null | undefined;
  /** The page's launch rule state — the same one the tick and Launch read. */
  launchHours: LaunchHoursState;
  launchLabel: string;
  /** The editor's refusal or late answer. */
  notice: string | null;
  organisationTwins: OrganisationTwins;
  onRetryOrganisationTwins: () => void;
  readTwinHours: (bid: string, signal?: AbortSignal) => Promise<ReadOpeningHoursOutcome>;
  /** Save a preset week, titled by its card. */
  onStart: (title: string, days: AuthoredOpeningHoursDay[]) => Promise<HoursActionResult>;
  onCopy: (source: CopySource) => Promise<HoursActionResult>;
  onLateProblem: (sentence: string) => void;
  /** *Start on a later date instead*: the step's `NewStandardHours`. */
  laterDate: React.ReactNode;
  /** The first card, for the step to focus when the panel appears after a Remove. */
  firstCardRef: React.Ref<HTMLButtonElement>;
  className?: string;
}

interface Preset {
  title: string;
  description: string;
  icon: IconName;
  /** A fresh week on each call. */
  days: () => AuthoredOpeningHoursDay[];
}

/** Frame 05's first three quick starts, in its order (R2.4, R3.1). */
export const PRESETS: readonly Preset[] = [
  {
    title: 'Same every day',
    description: 'One opening and closing time for all seven days.',
    icon: 'clock',
    days: sameEveryDay,
  },
  {
    title: 'Weekdays and weekends',
    description: 'One time Monday to Friday, another for Saturday and Sunday.',
    icon: 'calendar-week',
    days: weekdaysAndWeekends,
  },
  {
    title: 'Open 24/7',
    description: 'Every day open around the clock, midnight to midnight.',
    icon: 'infinity',
    days: open247,
  },
];

const StartPanel: React.FC<StartPanelProps> = ({
  twinName,
  timezone,
  launchHours,
  launchLabel,
  notice,
  organisationTwins,
  onRetryOrganisationTwins,
  readTwinHours,
  onStart,
  onCopy,
  onLateProblem,
  laterDate,
  firstCardRef,
  className,
}) => {
  const headingId = useId();
  const statusId = useId();
  /** What the pressed card says in its own description region: *Saving…*, or why it waited. */
  const [cardStatus, setCardStatus] = useState<{ title: string; text: string } | null>(null);

  const press = async (preset: Preset) => {
    setCardStatus({ title: preset.title, text: 'Saving…' });
    const result = await onStart(preset.title, preset.days());
    // On success the week replaces this panel; otherwise the step shows the sentence, except a
    // press refused while a save runs, which the card says itself (R3.5).
    setCardStatus(
      result.status === 'refused' && result.message === HOURS_STILL_SAVING
        ? { title: preset.title, text: HOURS_STILL_SAVING }
        : null,
    );
  };

  const note = startNoteFor(launchHours, launchLabel);
  const hereZone = timezone ?? null;

  return (
    <Card surface="on-scene" size="lg" className={className}>
      <section aria-labelledby={headingId}>
        <div className="intro">
          <IconDisc tone="aqua" size={72}>
            <Icon name="clock" size="xl" />
          </IconDisc>
          <h2 id={headingId}>
            {twinName ? `When is ${twinName} open?` : 'When is this twin open?'}
          </h2>
          <p className="lede">
            {timezone ? (
              <>
                Every visit count and report is measured against these hours, in{' '}
                <strong>{timezone}</strong>. Pick a starting point and adjust any day after.
              </>
            ) : (
              'Every visit count and report is measured against these hours, in the twin’s own timezone. Pick a starting point and adjust any day after.'
            )}
          </p>
        </div>

        {notice && (
          <Note tone="error" icon={<Icon name="circle-exclamation" size="xs" />} className="notice">
            {notice}
          </Note>
        )}

        <div className="starts">
          {PRESETS.map((p, i) => {
            const status = cardStatus?.title === p.title ? cardStatus.text : null;
            return (
              <ActionCard
                key={p.title}
                ref={i === 0 ? firstCardRef : undefined}
                icon={p.icon}
                tone="aqua"
                title={p.title}
                description={p.description}
                aria-describedby={status ? `${statusId}-${i}` : undefined}
                onClick={() => void press(p)}
              >
                <MiniWeek days={miniWeekOf(p.days())} />
              </ActionCard>
            );
          })}
          <CopyFromTwin
            organisationTwins={organisationTwins}
            hereZone={hereZone}
            onRetryTwins={onRetryOrganisationTwins}
            readTwinHours={readTwinHours}
            onCopy={onCopy}
            onLateProblem={onLateProblem}
          />
        </div>
        {/* Each card's own status, mounted before it fills so a change is heard (Block A.4). */}
        {PRESETS.map((p, i) => (
          <span key={p.title} id={`${statusId}-${i}`} role="status" style={visuallyHidden}>
            {cardStatus?.title === p.title ? cardStatus.text : ''}
          </span>
        ))}

        <div className="later">{laterDate}</div>

        {note && (
          <Note tone="error" icon={<Icon name="rocket" size="xs" />} className="launch-note">
            {note}
          </Note>
        )}
      </section>
    </Card>
  );
};

export default styled(StartPanel)`
  /* Frame 05: one glass panel (the design system's Card on-scene, lg, whose radius is radius.xl),
     its content centred in a column 22 px apart. The region and its name stay on the section. */
  justify-content: center;
  padding: ${space[6]} ${space[4]};

  > section {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${space[5]};
  }

  .intro {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${space[2]};
    text-align: center;
  }

  h2 {
    margin: 0;
    ${typeRole.title3};
    color: ${semantic.text.primary};
  }

  .lede {
    margin: 0;
    /* Frame 05: the sentence is at most 620 px wide — layout geometry. */
    max-width: 620px;
    ${typeRole.body1};
    color: ${semantic.text.secondary};
    text-wrap: pretty;
  }

  .lede strong {
    color: ${semantic.text.primary};
  }

  .starts {
    display: grid;
    /* Frame 05: four 220 px cards 12 px apart — four across at its width, wrapping narrower. */
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: ${space[3]};
    width: 100%;
    max-width: calc(4 * 220px + 3 * ${space[3]});
  }

  .notice {
    width: 100%;
    max-width: calc(4 * 220px + 3 * ${space[3]});
  }

  /* Frame 05: the launch note is as wide as its sentence, centred under the cards. */
  .launch-note {
    max-width: calc(4 * 220px + 3 * ${space[3]});
  }
`;
